import { http } from '@/lib/http'
import { ApiError } from '@/lib/api'
import type { AccountStats, Address, KycStatus, NotificationPrefGroup, ProfileType, ProfileUpdateInput, UserSession } from '@/types/models'

const notFoundAsNull = (e: unknown) => {
  if (e instanceof ApiError && e.status === 404) return null
  throw e
}

export const profileApi = {
  /** GET /profile — the full user (same shape as /auth/me plus middleName). */
  get: () => http.get<UserSession>('/profile'),
  // PUT /profile — partial. `data` holds a new token pair only on a role switch (the role lives in the JWT).
  // Sending `email` triggers the welcome / verification email.
  update: (body: ProfileUpdateInput) => http.put<{ accessToken?: string; refreshToken?: string } | null>('/profile', body),
  /** PATCH /profile/address — partial; only non-empty fields are written, status resets to pending. */
  updateAddress: (address: Partial<Address>) => http.patch<null>('/profile/address', address),
  /** GET /account-stats — landlord only. */
  stats: () => http.get<AccountStats>('/account-stats'),
  /** GET /document-kyc — the identity-document record, or null when none was ever submitted. */
  documentKyc: () => http.get<{ status?: string } | null>('/document-kyc').catch(notFoundAsNull),
  /** GET /banking/status — landlord only: `not_started | pending | processing | completed | failed`. */
  bankingStatus: () => http.get<{ status?: string } | null>('/banking/status').catch(notFoundAsNull),
  /** GET /profile/notification-preferences → { groups: [{ key, enabled }] } — the server owns the list. */
  notificationPrefs: () => http.get<{ groups: NotificationPrefGroup[] }>('/profile/notification-preferences').then((r) => r?.groups ?? []),
  /** PUT /profile/notification-preferences { enabled: [keys] } — replaces the whole set. */
  setNotificationPrefs: (enabledKeys: string[]) => http.put<null>('/profile/notification-preferences', { enabled: enabledKeys }),
}

/** The mobile `KYCStatusEnum.fromKey`: empty / not_started → notStarted, verified → completed, unknown → pending. */
export function kycStatusFrom(raw: string | undefined | null): KycStatus {
  if (!raw || raw === 'not_started') return 'notStarted'
  if (raw === 'verified' || raw === 'completed') return 'completed'
  if (raw === 'processing') return 'processing'
  if (raw === 'failed') return 'failed'
  return 'pending'
}

export const otherProfile = (p: ProfileType): ProfileType => (p === 'landlord' ? 'tenant' : 'landlord')
